/**
 * 供給地点マスタ CSV アップロード画面 Step1「ファイル選択」パネル内の
 * 記入ガイド表・雛形データ (common.md AC-DUC-037 / AC-DUC-031)。
 *
 * - 記入ガイド表 (`CIS_CONTRACT_CSV_COLUMN_GUIDE`): 列名 / 必須 / 説明 /
 *   入力形式 / 記入例。**文言の正本は spec `supply-point-master.md` 第1.5節
 *   「画面表示（記入ガイド表）」の表**で、第1節の「形式・値域」列にある内部参照
 *   （列番号参照・AC番号参照）は画面に出さない粒度に留める (spec §1.5
 *   ビジネスルール)。実装側で独自の説明文を持たない — 文言変更は spec の更新を
 *   もって行う。**括弧のみ design 規約 (`design/CLAUDE.md` 括弧は半角 + 前に
 *   半角スペース1つ) に合わせて半角化する** (spec 本文は全角、プロト UI は
 *   半角に統一する既定方針。それ以外の文言は spec の逐語)。事業者コード行の
 *   入力形式は #6334 (2026-08-26 PdM 判断) で確定した最新文言 (「空欄の場合は
 *   補完しない」) を使う — 旧文言 (「省略した場合は既存の登録値を保持する」)
 *   を持ち込まないこと。列順は `CIS_CONTRACT_CSV_HEADER` と完全一致させる。
 * - 雛形の記入例行 (`buildCisContractCsvTemplate`): spec 第1節「雛形記入例データ
 *   （イメージ）」の代表 2 パターン（① 通常の契約 / ② 契約変更〈旧行の終了日
 *   更新 + 新しい開始日の行〉）= 3 行。マスタ依存列（エリア・電圧階級）には
 *   ダミー値「記入例」を入れる。そのまま入稿してもマスタ検証（AC-DUI-002 /
 *   AC-DUI-003）で行エラーになり誤登録されない (spec §1「雛形の記入例」)。
 *   事業者コード・delete 列は空欄（spec の記入例表がこの 2 列に値を定義して
 *   いないため）。列順の SoT は `~/models/cisContractCsvHeader` の
 *   `CIS_CONTRACT_CSV_COLUMNS` / `serializeCisContractCsvTemplateRows`
 *   （bilateral 側の `BILATERAL_TRADE_CSV_COLUMNS` /
 *   `serializeBilateralTradeCsvRows` と同じ構造）— 本ファイルは列名 key の row
 *   を作るだけで、CSV セルの並びを手書きしない。
 */
import {
  CIS_CONTRACT_CSV_COLUMNS_EN,
  type CisContractCsvColumn,
  serializeCisContractCsvTemplateRows,
} from '~/models/cisContractCsvHeader'
import type { ColumnGuideEntry } from '~/models/columnGuide'

/**
 * 記入ガイド表の単一 SoT (spec §1.5 の表。括弧のみ design 規約で半角化)。
 * `ColumnGuideEntry<CisContractCsvColumn>` で「ガイド行名 ∈ CSV 列名」の
 * 不変条件を型で保つ (issue #6467 レビュー指摘、bilateral 側と同判断)。
 */
export const CIS_CONTRACT_CSV_COLUMN_GUIDE: Array<
  ColumnGuideEntry<CisContractCsvColumn>
> = [
  {
    name: '供給地点特定番号',
    description: '供給地点を識別するコード (CIS の locationCode)',
    required: 'required',
    rule: '22桁の数字で入力 (先頭ゼロを含む場合もそのまま保持)。先頭2桁はエリアの番号 (例: 東京は 03)。',
    example: '0300000000000012345001',
  },
  {
    name: 'エリア',
    description: '供給地点のエリア',
    required: 'required',
    rule: '「北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州 / 沖縄」のいずれかを入力。',
    example: '東京',
  },
  {
    name: '契約種別コード',
    description: 'CIS 側の契約種別を表すコード',
    required: 'required',
    rule: 'CIS のエクスポート値をそのまま入力 (eBalance 側でマスタ照合は行わない)。64文字以内で入力。',
    example: 'P1L0000023',
  },
  {
    name: '電圧階級',
    description: '供給地点の電圧階級',
    required: 'required',
    rule: '「特別高圧 / 高圧 / 低圧」のいずれかを入力。',
    example: '低圧',
  },
  {
    name: '開始日',
    description: '当該契約内容が適用される開始日',
    required: 'required',
    rule: '「YYYY/MM/DD」の形式で入力。',
    example: '2026/04/01',
  },
  {
    name: '終了日',
    description: '当該契約内容が適用される終了日',
    required: 'optional',
    rule: '「YYYY/MM/DD」の形式で入力、または終了日未定の場合は「9999/12/31」を入力。空欄も現在も有効として扱われる。',
    example: '9999/12/31',
  },
  {
    name: '事業者コード',
    description: '供給地点の帰属事業者 (小売) を表すコード',
    required: 'optional',
    rule: 'OCCTO 事業者コード体系の4桁コードで入力。空欄の場合は補完しない (既存の登録値を保持する。未登録なら「帰属不明」のまま)。記載した時のみデータを上書きする。',
    example: 'R001',
  },
  {
    name: 'delete',
    description: '論理削除フラグ',
    required: 'optional',
    rule: '入力済みのデータを削除したい場合は「1」で入力。供給地点特定番号と開始日を照合して既存データがある場合は削除する。',
    example: '1',
  },
]

type CisContractCsvColumnEn = (typeof CIS_CONTRACT_CSV_COLUMNS_EN)[number]

/**
 * 英語表示の記入ガイド表 (spec §1.5「英語設定時は」+ 付録「表示文言（英語）」の逐語)。
 *
 * 列名は**雛形の英語ヘッダー (`CIS_CONTRACT_CSV_COLUMNS_EN`) と同じ値**にする — ダウンロードした
 * 雛形のヘッダーと画面のガイド表の列名を一致させるのが目的 (#8673)。区分値 (エリア・電圧階級) は
 * 英語の値で案内する (`conventions.md`「CSV の英語表示」)。行の順・必須区分・記入例のうち
 * 区分値以外は日本語の表 ([[CIS_CONTRACT_CSV_COLUMN_GUIDE]]) と同じ。
 */
const CIS_CONTRACT_CSV_COLUMN_GUIDE_EN: Array<
  ColumnGuideEntry<CisContractCsvColumnEn>
> = [
  {
    name: 'SupplyPointSpecificNumber',
    description: 'Code that identifies the supply point (locationCode in CIS)',
    required: 'required',
    rule: 'Enter a 22-digit number. Keep any leading zeros. The first 2 digits are the area number (for example, 03 for Tokyo).',
    example: '0300000000000012345001',
  },
  {
    name: 'Area',
    description: 'Area of the supply point',
    required: 'required',
    rule: 'Enter one of Hokkaido / Tohoku / Tokyo / Chubu / Hokuriku / Kansai / Chugoku / Shikoku / Kyushu / Okinawa.',
    example: 'Tokyo',
  },
  {
    name: 'CisTariffCode',
    description: 'Code for the energy contract type in CIS',
    required: 'required',
    rule: 'Enter the value exported from CIS as is (eBalance does not check it against a master). Use up to 64 characters.',
    example: 'P1L0000023',
  },
  {
    name: 'VoltageClass',
    description: 'Voltage class of the supply point',
    required: 'required',
    rule: 'Enter one of Extra-high voltage / High voltage / Low voltage.',
    example: 'Low voltage',
  },
  {
    name: 'StartDate',
    description: 'Date from which the contract terms apply',
    required: 'required',
    rule: 'Enter in YYYY/MM/DD format.',
    example: '2026/04/01',
  },
  {
    name: 'EndDate',
    description: 'Date until which the contract terms apply',
    required: 'optional',
    rule: 'Enter in YYYY/MM/DD format, or enter 9999/12/31 if the end date is not yet decided. A blank value is also treated as still in effect.',
    example: '9999/12/31',
  },
  {
    name: 'BusinessCode',
    description: 'Code of the retail provider that the supply point belongs to',
    required: 'optional',
    rule: 'Enter a 4-character code from the OCCTO provider code system. If left blank, nothing is filled in (the registered value is kept; if none is registered, it stays Unattributed). The data is overwritten only when a value is entered.',
    example: 'R001',
  },
  {
    name: 'Delete',
    description: 'Deletion flag',
    required: 'optional',
    rule: 'To delete registered data, enter 1. If existing data matches the supply point specific number and start date, it is deleted.',
    example: '1',
  },
]

/** 表示言語に応じた記入ガイド表 (spec §1.5)。 */
export function cisContractCsvColumnGuide(
  isEnglish: boolean,
): Array<ColumnGuideEntry<CisContractCsvColumn | CisContractCsvColumnEn>> {
  return isEnglish
    ? CIS_CONTRACT_CSV_COLUMN_GUIDE_EN
    : CIS_CONTRACT_CSV_COLUMN_GUIDE
}

/**
 * 記入例案内 (AC-DUC-035) の代表列と値。代表列は主キーの供給地点特定番号で、英語表示では
 * 雛形の英語ヘッダー名にする (#8673)。
 */
export function cisContractCsvSampleRow(isEnglish: boolean): {
  column: string
  example: string
} {
  return {
    column: isEnglish
      ? CIS_CONTRACT_CSV_COLUMN_GUIDE_EN[0].name
      : CIS_CONTRACT_CSV_COLUMN_GUIDE[0].name,
    example: '0300000000000012345001',
  }
}

/**
 * 雛形の記入例データ (spec §1「雛形記入例データ（イメージ）」の3行を逐語)。
 * key は `CisContractCsvColumn` (CSV 列名) — 列順は
 * `serializeCisContractCsvTemplateRows` が `CIS_CONTRACT_CSV_COLUMNS` から
 * 導出するため、ここでは列の並びを意識しない。事業者コード・delete は省略
 * (= serializer が空欄で埋める。`Partial` 型で受けるため省略しても型エラーに
 * ならない — テンプレート側は記入例が一部列を省略する運用のため意図的)。
 *
 * エリア・電圧階級には取込が受理しないダミー値 `dummy` を入れ、記入例行を残して
 * 入稿しても行エラーになるようにする (spec §1「雛形の記入例」)。ダミー値は日本語
 * 表示で「記入例」、英語表示で英語付録の `Example` (英語の区分値にも無い値)。
 */
// 日本語の記入例の値は backend apitest が逐語で写している
// (go/test/apitest/cis_contract_import_test.go
// TestImportCisContracts_TemplateSampleRowsUnchanged_AllRowsErrorNoPartialSuccess、
// issue #7816)。ここを変えたら Go 側も同じ値に揃える。英語のダミー値 `Example` が
// 取込で行エラーになることは backend の unit test (`ciscontract/csv_test.go`) が固定する。
function cisContractCsvTemplateRows(
  dummy: string,
): Array<Partial<Record<CisContractCsvColumn, string>>> {
  return [
    // ①（通常の契約）: 開始日から終了日未定 (9999/12/31) までの通常契約を1行で表現する
    {
      供給地点特定番号: '0300000000000012345001',
      エリア: dummy,
      契約種別コード: 'P1L0000023',
      電圧階級: dummy,
      開始日: '2026/04/01',
      終了日: '9999/12/31',
    },
    // ②（契約変更、旧行の終了日更新）: 契約種別変更時、旧行の終了日を変更日の前日に更新する行
    {
      供給地点特定番号: '0300000000000012345002',
      エリア: dummy,
      契約種別コード: 'P1L0000018',
      電圧階級: dummy,
      開始日: '2025/10/01',
      終了日: '2026/09/30',
    },
    // ②（契約変更、新しい開始日の行）: 新しい開始日からの新契約を表す行 (旧行と2行1組で投入する)
    {
      供給地点特定番号: '0300000000000012345002',
      エリア: dummy,
      契約種別コード: 'P1L0000031',
      電圧階級: dummy,
      開始日: '2026/10/01',
      終了日: '9999/12/31',
    },
  ]
}

/**
 * 雛形ダウンロード用の CSV 本文を組み立てる (ヘッダー行 + 記入例3行、
 * common.md AC-DUC-031。英語表示はヘッダー行を英語ヘッダー名、ダミー値を英語にする
 * 〈common.md §6〉)。列順・エスケープ・改行規約は
 * `~/models/cisContractCsvHeader` の `serializeCisContractCsvTemplateRows`
 * (単一 SoT) に委譲する。
 */
export function buildCisContractCsvTemplate(isEnglish: boolean): string {
  return serializeCisContractCsvTemplateRows(
    cisContractCsvTemplateRows(isEnglish ? 'Example' : '記入例'),
    isEnglish,
  )
}
